import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  CheckCircle2,
  CreditCard,
  Info,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { SummaryTiles, IconAction, PenaltyPanel, downloadClientDocument } from "../components/client-portal/PaymentBits";
import { money, parseDay } from "../components/client-portal/format";

// Same money format as the cabinet: "222 275 UAH" — the currency is always
// spelled out, never a lone symbol that is easy to misread.
const fmtMoney = (amount, currency = "USD") => money(amount, currency);
const fmtUsd = (n) => `$${Math.round(n || 0).toLocaleString("en-US")}`;

// This page is shared by two audiences on two routes: the client cabinet
// (/client/payments) is intentionally Ukrainian; the partner cabinet
// (/trp/payments) must be English. All user-facing copy therefore lives in this
// dictionary and is picked by route below — never hardcode a string in JSX.
const I18N = {
  uk: {
    eyebrow: "Оплата",
    sub: "Усі рахунки по всіх ваших проєктах. Суми — у валюті рахунку.",
    tileOverdue: "Прострочено",
    tileDue: "Оплатити в строк",
    tileAwaiting: "Ще не виставлено",
    tilePaid: "Оплачено",
    nothing: "Нічого",
    penaltyHint: (amt, perDay) => `пеня ${amt}${perDay ? ` · +${perDay}/день` : ""}`,
    dueHint: (d) => `найближчий до ${d}`,
    awaitingHint: "платити поки не потрібно",
    paidHint: (n) => `${n} рахунків`,
    billTo: "Рахунок на",
    billToAll: "Платник за всіма рахунками",
    invoiceNo: "№",
    rowOverdue: (n, d) => `Прострочено на ${n} дн. (до ${d})`,
    rowDue: (d, n) => `Оплатіть до ${d}${n != null ? ` · ще ${n} дн.` : ""}`,
    rowPenalty: (amt) => `пеня ${amt}`,
    rowGrace: "пеня ще не нараховується",
    rowGraceIn: (n) => `пеня почне нараховуватись через ${n} дн.`,
    rowAwaiting: "Рахунок ще не надіслано — платити поки не потрібно",
    rowPaid: (d) => `Оплачено ${d}`,
    payByTransfer: "Оплата — переказом за реквізитами з рахунку, виставленого за договором. Оплата карткою — лише за посиланням від менеджера.",
    cardLink: "Оплатити карткою (посилання від менеджера)",
    invoiceDl: "Завантажити рахунок",
    invoiceNotReady: "Рахунок ще готує менеджер — реквізити для цього платежу не заповнені",
    issuer: "Отримувач",
    payer: "Платник",
    invoiceEarly: "Рахунок (ще не надіслано — можна завантажити заздалегідь)",
    actDl: "Акт виконаних робіт",
    heading: "Рахунки до сплати",
    asOf: (d) => `Станом на ${d}`,
    refresh: "Оновити",
    due: "До сплати",
    overdue: "Прострочено",
    penalty: "Пеня",
    penaltyInfoSuffix: " (інфо)",
    awaitingTitle:
      "Неоплачена робота, яку ще формально не виставили — без пені, платити нічого, поки не надішлемо рахунок.",
    awaiting: "Очікує рахунку",
    paid: "Оплачено",
    penaltyBannerDisabled:
      "Нарахування пені за прострочення вимкнено на рівні порталу — пеня показана довідково, не стягується.",
    overdueAmountDue: "Прострочено до сплати",
    invoicesOldest: (n, d) => `${n} рахунок(ів) · найстаріший ${d}д тому`,
    penaltyAccruing: "Нараховується пеня",
    perDaySuffix: (rate) => `щодня · ${rate}%/день на кожен несплачений рахунок`,
    graceMore: (n, g) =>
      `ще ${n} рахунок(ів) почнуть нараховувати пеню після ${g}-денного пільгового періоду`,
    payTodayStops: "Сплатіть сьогодні — лічильник зупиниться.",
    tomorrowPenalty: (amt) => `Завтра: ${amt} пені`,
    payOverdue: "Сплатити прострочене",
    tabDue: (n) => `До сплати (${n})`,
    tabAwaiting: (n) => `Очікує рахунку (${n})`,
    tabPaid: (n) => `Оплачено (${n})`,
    selected: "обрано",
    invoices: "Рахунки",
    mixedCurrency: "Різні валюти — оплачуйте рахунки окремими партіями.",
    clear: "Скинути",
    payPenalty: "Сплатити пеню",
    openingStripe: "Відкриваємо Stripe…",
    payInvoices: "Сплатити рахунки",
    allClear: "Все чисто",
    noInvoicesAwaiting:
      "Виставлених рахунків немає — є робота, що очікує рахунку (сусідня вкладка).",
    noInvoicesDue: "Немає рахунків до сплати.",
    colStatus: "Статус",
    colInvoice: "Рахунок",
    colProject: "Проєкт",
    colAmount: "Сума",
    colDue: "Термін",
    colPenalty: "Пеня",
    colActions: "Дії",
    payPenaltySeparately: "Сплатити пеню за прострочення окремо",
    pay: "Сплатити",
    awaitingSub: "— робота, яку ми ще не виставили · без пені, платити поки нічого",
    colService: "Послуга",
    noPaymentsYet: "Оплат ще немає.",
    colPaidDate: "Оплачено",
    daysAgo: (n) => `(${n}д тому)`,
    loading: "Завантаження платежів…",
    baseContract: "суму договору",
    baseInvoice: "суму рахунку",
    penaltyCharged: "Стягується",
    penaltyInfoRef: "Довідково (пеня на порталі вимкнена)",
    penaltyTooltip: (p, baseLabel, enforced) =>
      `${p.rate_per_day}%/день × ${p.days_charged} дн. на ${baseLabel} ($${(
        p.base_amount || 0
      ).toLocaleString()}) = ${fmtUsd(p.amount)}. Пільговий період ${
        p.grace_period_days
      }д. Днів прострочки: ${p.days_overdue}. ${enforced}.`,
    penaltyDisabledTip: "Нарахування пені вимкнено на рівні порталу.",
    penaltyGraceTip: (n) => `Пені ще немає — у межах ${n}-денного пільгового періоду.`,
    statusOverdue: (n) => `Прострочено +${n}д`,
    statusDueIn: (n) => `Термін через ${n}д`,
    failedToLoad: "Не вдалося завантажити",
    stripeNoUrl: "Stripe не повернув посилання на оплату",
    toastPenaltyPaid: "Оплату пені отримано — баланси оновляться незабаром.",
    toastPaid: "Оплату отримано — рахунки буде позначено оплаченими незабаром.",
    toastCancelled: "Оплату скасовано — змін не внесено.",
    failedToOpenPayment: "Не вдалося відкрити оплату",
  },
  en: {
    eyebrow: "Payments",
    sub: "All invoices across your projects. Amounts are in the invoice currency.",
    tileOverdue: "Overdue",
    tileDue: "Due on time",
    tileAwaiting: "Not invoiced yet",
    tilePaid: "Paid",
    nothing: "Nothing",
    penaltyHint: (amt, perDay) => `penalty ${amt}${perDay ? ` · +${perDay}/day` : ""}`,
    dueHint: (d) => `nearest by ${d}`,
    awaitingHint: "nothing to pay yet",
    paidHint: (n) => `${n} invoices`,
    billTo: "Billed to",
    billToAll: "Payer on all invoices",
    invoiceNo: "#",
    rowOverdue: (n, d) => `Overdue ${n}d (was due ${d})`,
    rowDue: (d, n) => `Pay by ${d}${n != null ? ` · ${n}d left` : ""}`,
    rowPenalty: (amt) => `penalty ${amt}`,
    rowGrace: "no penalty yet",
    rowGraceIn: (n) => `penalty starts in ${n}d`,
    rowAwaiting: "Not invoiced yet — nothing to pay for now",
    rowPaid: (d) => `Paid ${d}`,
    payByTransfer: "Pay by bank transfer using the details on the invoice issued under the contract. Card payment only via a link from your manager.",
    cardLink: "Pay by card (link from your manager)",
    invoiceDl: "Download invoice",
    invoiceNotReady: "Your manager is still preparing this invoice",
    issuer: "Payee",
    payer: "Payer",
    invoiceEarly: "Invoice (not sent yet — you can download it in advance)",
    actDl: "Certificate of completion",
    heading: "Payments due",
    asOf: (d) => `As of ${d}`,
    refresh: "Refresh",
    due: "Due",
    overdue: "Overdue",
    penalty: "Penalty",
    penaltyInfoSuffix: " (info)",
    awaitingTitle:
      "Unpaid work not yet formally invoiced — no penalty, nothing to pay until we send an invoice.",
    awaiting: "Awaiting invoice",
    paid: "Paid",
    penaltyBannerDisabled:
      "Late-payment penalty accrual is disabled at the portal level — penalty shown for reference only, not charged.",
    overdueAmountDue: "Overdue amount due",
    invoicesOldest: (n, d) => `${n} invoice(s) · oldest ${d}d ago`,
    penaltyAccruing: "Penalty accruing",
    perDaySuffix: (rate) => `daily · ${rate}%/day on each unpaid invoice`,
    graceMore: (n, g) =>
      `another ${n} invoice(s) will start accruing penalty after the ${g}-day grace period`,
    payTodayStops: "Pay today — the counter stops.",
    tomorrowPenalty: (amt) => `Tomorrow: ${amt} penalty`,
    payOverdue: "Pay overdue",
    tabDue: (n) => `Due (${n})`,
    tabAwaiting: (n) => `Awaiting invoice (${n})`,
    tabPaid: (n) => `Paid (${n})`,
    selected: "selected",
    invoices: "Invoices",
    mixedCurrency: "Mixed currencies — pay invoices in separate batches.",
    clear: "Clear",
    payPenalty: "Pay penalty",
    openingStripe: "Opening Stripe…",
    payInvoices: "Pay invoices",
    allClear: "All clear",
    noInvoicesAwaiting:
      "No invoices issued — there is work awaiting an invoice (adjacent tab).",
    noInvoicesDue: "No invoices due.",
    colStatus: "Status",
    colInvoice: "Invoice",
    colProject: "Project",
    colAmount: "Amount",
    colDue: "Due date",
    colPenalty: "Penalty",
    colActions: "Actions",
    payPenaltySeparately: "Pay the late penalty separately",
    pay: "Pay",
    awaitingSub: "— work we haven't invoiced yet · no penalty, nothing to pay for now",
    colService: "Service",
    noPaymentsYet: "No payments yet.",
    colPaidDate: "Paid",
    daysAgo: (n) => `(${n}d ago)`,
    loading: "Loading payments…",
    baseContract: "the contract amount",
    baseInvoice: "the invoice amount",
    penaltyCharged: "Charged",
    penaltyInfoRef: "Reference only (penalty disabled on portal)",
    penaltyTooltip: (p, baseLabel, enforced) =>
      `${p.rate_per_day}%/day × ${p.days_charged} days on ${baseLabel} ($${(
        p.base_amount || 0
      ).toLocaleString()}) = ${fmtUsd(p.amount)}. Grace period ${
        p.grace_period_days
      }d. Days overdue: ${p.days_overdue}. ${enforced}.`,
    penaltyDisabledTip: "Penalty accrual is disabled at the portal level.",
    penaltyGraceTip: (n) => `No penalty yet — within the ${n}-day grace period.`,
    statusOverdue: (n) => `Overdue +${n}d`,
    statusDueIn: (n) => `Due in ${n}d`,
    failedToLoad: "Failed to load",
    stripeNoUrl: "Stripe didn't return a checkout URL",
    toastPenaltyPaid: "Penalty payment received — balances will update soon.",
    toastPaid: "Payment received — invoices will be marked paid soon.",
    toastCancelled: "Payment cancelled — no changes made.",
    failedToOpenPayment: "Failed to open payment",
  },
};

const MyPaymentSchedulePage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [paying, setPaying] = useState(false);
  const [tab, setTab] = useState("outstanding");
  const location = useLocation();
  const navigate = useNavigate();
  // Partner cabinet lives under /trp/* → English; the client cabinet is Ukrainian.
  const lang = location.pathname.startsWith("/trp") ? "en" : "uk";
  const t = I18N[lang];

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/my-payment-schedule");
      setData(res.data);
      // Drop any selected ids that no longer exist in the fresh outstanding set.
      const liveIds = new Set([
        ...(res.data?.buckets?.overdue || []).map((r) => r.id),
        ...(res.data?.buckets?.pending || []).map((r) => r.id),
      ]);
      setSelected((prev) => new Set([...prev].filter((id) => liveIds.has(id))));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || t.failedToLoad);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // If nothing is formally invoiced but there IS unpaid "awaiting invoice" work,
  // jump straight to that tab instead of showing an empty "Due" tab.
  useEffect(() => {
    if (!data) return;
    const outCount =
      (data.buckets?.overdue?.length || 0) + (data.buckets?.pending?.length || 0);
    const awCount = data.buckets?.awaiting_invoice?.length || 0;
    if (tab === "outstanding" && outCount === 0 && awCount > 0) setTab("awaiting");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Surface a toast when the user lands back from Stripe so the redirect feels intentional.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("paid") === "1") {
      const kind = params.get("kind") || "invoice";
      toast.success(kind === "penalty" ? t.toastPenaltyPaid : t.toastPaid);
      navigate(location.pathname, { replace: true });
    } else if (params.get("cancelled") === "1") {
      toast.info(t.toastCancelled);
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const outstanding = useMemo(() => {
    if (!data) return [];
    // Single combined table: overdue first (sorted by lateness desc), then within-terms
    // (sorted by days-left asc). The status badge differentiates them.
    return [...(data.buckets?.overdue || []), ...(data.buckets?.pending || [])];
  }, [data]);

  const paid = data?.buckets?.recently_received || [];
  // Unpaid work that hasn't been formally invoiced yet — shown read-only so the
  // page is never empty while real work is owed (no penalty, nothing to pay yet).
  const awaiting = data?.buckets?.awaiting_invoice || [];
  const penalty_settings = data?.penalty_settings;

  // Debt hero figures — all in USD (penalty is always USD-canonical on the backend).
  const debt = useMemo(() => {
    const overdueRows = data?.buckets?.overdue || [];
    let perDay = 0; // current daily accrual across all charging invoices
    let accrued = 0; // unpaid penalty accrued so far
    let inGrace = 0; // overdue but still inside grace period
    let maxLate = 0;
    for (const r of overdueRows) {
      maxLate = Math.max(maxLate, r.days_overdue || 0);
      const p = r.penalty;
      if (p && !r.penalty_paid_at) {
        accrued += p.amount || 0;
        perDay += ((p.base_amount || 0) * (p.rate_per_day || 0)) / 100;
      } else if (!p) {
        inGrace += 1;
      }
    }
    return { overdueRows, perDay, accrued, inGrace, maxLate };
  }, [data]);

  // Currency consistency: Stripe checkout requires a single currency per session, so
  // we disable any selection that mixes currencies and tell the user why.
  const selectedRows = useMemo(
    () => outstanding.filter((r) => selected.has(r.id)),
    [outstanding, selected]
  );
  const selectedCurrencies = new Set(selectedRows.map((r) => r.currency || "USD"));
  const mixedCurrency = selectedCurrencies.size > 1;
  const selectedTotal = selectedRows.reduce((sum, r) => sum + (r.amount || 0), 0);
  const selectedCurrency = selectedRows[0]?.currency || "USD";
  // Penalty bulk-pay across selection — independent of currency mix because
  // backend bills all penalties in USD.
  const selectedPenaltyRows = selectedRows.filter(
    (r) => r.penalty?.amount > 0 && !r.penalty_paid_at
  );
  const selectedPenaltyTotal = selectedPenaltyRows.reduce(
    (sum, r) => sum + (r.penalty?.amount || 0),
    0
  );

  const toggleOne = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === outstanding.length ? new Set() : new Set(outstanding.map((r) => r.id))
    );
  };

  // kind = "invoice" pays the main service amount; "penalty" pays only the late fee.
  // Penalty flow is deliberately separate so the audience can settle each without
  // bundling them — the penalty feature is still in test mode and shouldn't be
  // forced on the main charge.
  const payIds = async (ids, kind = "invoice") => {
    if (!ids.length) return;
    setPaying(true);
    try {
      const res = await api.post("/my-payments/checkout", {
        service_ids: ids,
        origin_url: window.location.origin,
        kind,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error(t.stripeNoUrl);
        setPaying(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.failedToOpenPayment);
      setPaying(false);
    }
  };

  if (loading) return <div className="p-6 text-zinc-400">{t.loading}</div>;
  if (error)
    return (
      <div className="p-6">
        <div className="bg-red-950/40 border border-red-500/30 rounded p-4 text-red-300">
          {error}
        </div>
      </div>
    );
  if (!data) return null;

  const { summary } = data;
  const allSelected = outstanding.length > 0 && selected.size === outstanding.length;

  // ── Same visual language as the cabinet Payments block: summary tiles in the
  // invoice's own currency (never one "$" number over mixed currencies), and one
  // line per invoice — status · amount · documents · pay.
  // Shared parser: ISO and the older "15.07.2026" both work; unparsable → "—".
  const fmtD = (v) => {
    const d = parseDay(v);
    return d ? d.toLocaleDateString(lang === "uk" ? "uk-UA" : "en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
  };
  const sumNative = (rows) => {
    const by = {};
    rows.forEach((r) => {
      const c = r.currency || "USD";
      by[c] = (by[c] || 0) + (r.amount || 0);
    });
    const parts = Object.entries(by).filter(([, v]) => v > 0).map(([c, v]) => fmtMoney(v, c));
    return parts.length ? parts.join(" + ") : t.nothing;
  };
  const overdueRows = data.buckets?.overdue || [];
  const pendingRows = data.buckets?.pending || [];
  const nearestDue = pendingRows.map((r) => r.invoice_due_date).filter(Boolean).sort()[0];
  // One payer for everything (the usual case) → say it once above the list
  // instead of repeating it on every row; several payers → show it per row.
  const billTos = [...new Set([...outstanding, ...awaiting, ...paid].map((r) => r.bill_to).filter(Boolean))];
  const singleBillTo = billTos.length === 1 ? billTos[0] : null;

  // Overdue but still inside the grace window: say WHEN the penalty starts,
  // never "penalty accruing $0.00".
  const graceLeft = (row) =>
    (row.penalty?.grace_period_days ?? penalty_settings?.default_grace_period_days ?? 0) - (row.days_overdue || 0);
  // Clients pay by the contract's invoice (bank transfer); a card link exists
  // only when the manager created one. Self-serve card checkout stays for the
  // partner cabinet (/trp) only.
  const selfServeCard = data.audience !== "client";
  const statusOf = (row, kind) => {
    if (kind === "paid") return { text: parseDay(row.paid_date) ? t.rowPaid(fmtD(row.paid_date)) : t.paid, tone: "text-green-500", dot: "bg-green-500" };
    if (kind === "awaiting") return { text: t.rowAwaiting, tone: "text-zinc-500", dot: "bg-zinc-600" };
    if (row.days_overdue != null) {
      const pen = row.penalty?.amount > 0 && !row.penalty_paid_at
        ? ` · ${t.rowPenalty(fmtUsd(row.penalty.amount))}`
        : row.penalty_paid_at ? "" : ` · ${graceLeft(row) > 0 ? t.rowGraceIn(graceLeft(row)) : t.rowGrace}`;
      return { text: `${t.rowOverdue(row.days_overdue, fmtD(row.invoice_due_date))}${pen}`, tone: "text-red-500", dot: "bg-red-500" };
    }
    return { text: t.rowDue(fmtD(row.invoice_due_date), row.days_left), tone: "text-amber-500", dot: "bg-amber-500" };
  };

  const renderRow = (row, kind) => {
    const st = statusOf(row, kind);
    const selectable = kind === "outstanding" && selfServeCard;
    const isSelected = selected.has(row.id);
    const hasPenalty = (row.penalty?.amount || 0) > 0 && !row.penalty_paid_at;
    return (
      <div
        key={row.id}
        className={`grid grid-cols-[20px_minmax(0,1fr)_auto] md:grid-cols-[20px_minmax(0,1fr)_minmax(0,1.1fr)_140px_minmax(120px,auto)] items-center gap-x-3 gap-y-1 px-3 py-2.5 rounded-lg transition-colors ${
          isSelected ? "bg-orange-500/5" : "hover:bg-zinc-800/30"
        }`}
      >
        <div className="flex items-center justify-center">
          {selectable ? (
            <Checkbox checked={isSelected} onCheckedChange={() => toggleOne(row.id)} aria-label={row.name} />
          ) : (
            <span className={`w-2 h-2 rounded-full ${st.dot}`} />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm text-zinc-100 font-medium truncate" title={row.name}>
            {row.name}
            {row.invoice_number ? <span className="text-zinc-500 font-normal"> · {t.invoiceNo} {row.invoice_number}</span> : null}
          </p>
          <p className="text-xs text-zinc-500 truncate" title={`${row.project_name || ""} · ${t.billTo}: ${row.bill_to || "—"}`}>
            {row.project_name || "—"}
            {row.bill_to && !singleBillTo ? <> · {t.payer}: <span className="text-zinc-400">{row.bill_to}</span></> : null}
            {row.issuer ? <> · {t.issuer}: <span className="text-zinc-400">{row.issuer}</span></> : null}
          </p>
        </div>
        <p className={`col-start-2 row-start-2 md:col-start-auto md:row-start-auto text-xs truncate ${st.tone}`} title={st.text}>
          {st.text}
        </p>
        <div className="col-start-3 row-start-1 md:col-start-auto md:row-start-auto text-right">
          <p className="text-sm font-semibold text-zinc-100 tabular-nums whitespace-nowrap">{fmtMoney(row.amount, row.currency)}</p>
          {row.currency && row.currency !== "USD" && row.amount_usd ? (
            <p className="text-[10px] text-zinc-500 tabular-nums">≈ {fmtUsd(row.amount_usd)}</p>
          ) : null}
        </div>
        <div className="col-start-2 md:col-start-auto flex items-center gap-1.5 md:justify-end flex-wrap md:flex-nowrap">
          {row.stripe_url && !selfServeCard && (
            <IconAction kind="card" href={row.stripe_url} label={t.cardLink} />
          )}
          <IconAction kind="invoice" onClick={() => downloadClientDocument(row.id, "invoice", lang)}
            disabled={row.doc_ready === false}
            label={row.doc_ready === false ? t.invoiceNotReady : kind === "awaiting" ? t.invoiceEarly : t.invoiceDl} />
          {kind === "paid" && (
            <IconAction kind="act" onClick={() => downloadClientDocument(row.id, "act", lang)} label={t.actDl} />
          )}
          {kind === "outstanding" && selfServeCard && hasPenalty && (
            <Button
              size="sm"
              variant="ghost"
              disabled={paying}
              onClick={() => payIds([row.id], "penalty")}
              title={t.payPenaltySeparately}
              className="h-8 px-2 text-xs text-red-500 hover:bg-red-500/10"
            >
              {t.penalty}
            </Button>
          )}
          {kind === "outstanding" && selfServeCard && (
            <Button
              size="sm"
              disabled={paying}
              onClick={() => payIds([row.id])}
              className="h-8 px-2.5 text-xs gap-1 bg-orange-500 hover:bg-orange-600 text-white"
            >
              <CreditCard className="h-3 w-3" />
              {t.pay}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 font-semibold">{t.eyebrow}</p>
          <h1 className="text-2xl font-extrabold text-zinc-50 font-['Manrope'] tracking-tight mt-1">{t.heading}</h1>
          <p className="text-sm text-zinc-500 mt-1">{t.sub} {t.asOf(fmtD(data.as_of))}.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-2 h-8 rounded-full">
          <RefreshCw className="h-3.5 w-3.5" />
          {t.refresh}
        </Button>
      </div>

      <SummaryTiles
        tiles={[
          {
            label: t.tileOverdue,
            value: sumNative(overdueRows),
            tone: overdueRows.length ? "overdue" : "muted",
            hint: debt.accrued > 0 && penalty_settings?.enabled
              ? t.penaltyHint(fmtUsd(debt.accrued), debt.perDay ? fmtUsd(debt.perDay) : null)
              : null,
          },
          {
            label: t.tileDue,
            value: sumNative(pendingRows),
            tone: pendingRows.length ? "due" : "muted",
            hint: nearestDue ? t.dueHint(fmtD(nearestDue)) : null,
          },
          { label: t.tileAwaiting, value: sumNative(awaiting), tone: "muted", hint: awaiting.length ? t.awaitingHint : null },
          { label: t.tilePaid, value: sumNative(paid), tone: paid.length ? "paid" : "muted", hint: paid.length ? t.paidHint(paid.length) : null },
        ]}
      />

      {penalty_settings?.enabled && (
        <PenaltyPanel
          rule={{
            rate_per_day: penalty_settings.default_rate_per_day,
            grace_period_days: penalty_settings.default_grace_period_days,
            base: penalty_settings.default_base,
            enabled: true,
          }}
          rows={overdueRows.map((r) => ({ ...r, title: r.name, sub: r.project_name, graceLeft: graceLeft(r) }))}
        />
      )}

      {!penalty_settings?.enabled && summary.overdue_count > 0 && (
        <div className="flex items-start gap-2 text-xs text-zinc-400 border border-zinc-800 bg-zinc-900/40 rounded-lg px-3 py-2">
          <Info className="h-3.5 w-3.5 text-zinc-500 mt-0.5 shrink-0" />
          <span>{t.penaltyBannerDisabled}</span>
        </div>
      )}

      {singleBillTo && (
        <p className="text-sm text-zinc-500 -mt-1">
          {t.billToAll}: <span className="text-zinc-200 font-medium">{singleBillTo}</span>
        </p>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="outstanding">{t.tabDue(outstanding.length)}</TabsTrigger>
          {awaiting.length > 0 && <TabsTrigger value="awaiting">{t.tabAwaiting(awaiting.length)}</TabsTrigger>}
          <TabsTrigger value="paid">{t.tabPaid(paid.length)}</TabsTrigger>
        </TabsList>

        <TabsContent value="outstanding" className="space-y-2 mt-3">
          {selected.size > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border border-orange-500/40 bg-orange-500/5 rounded-xl px-3 py-2 text-sm">
              <div className="text-zinc-200 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span><span className="font-semibold">{selected.size}</span> {t.selected}</span>
                <span className="text-zinc-600">·</span>
                <span>{t.invoices} <span className="font-semibold tabular-nums">{fmtMoney(selectedTotal, selectedCurrency)}</span></span>
                {selectedPenaltyRows.length > 0 && (
                  <>
                    <span className="text-zinc-600">·</span>
                    <span>
                      {t.penalty} <span className="font-semibold text-red-500 tabular-nums">{fmtUsd(selectedPenaltyTotal)}</span>
                    </span>
                  </>
                )}
                {mixedCurrency && <span className="text-amber-500 text-xs">{t.mixedCurrency}</span>}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="h-8 text-zinc-400">
                  {t.clear}
                </Button>
                {selectedPenaltyRows.length > 0 && (
                  <Button size="sm" variant="outline" disabled={paying}
                    onClick={() => payIds(selectedPenaltyRows.map((r) => r.id), "penalty")}
                    className="h-8 border-red-500/40 text-red-500 hover:bg-red-500/10 gap-2">
                    <CreditCard className="h-3.5 w-3.5" />
                    {t.payPenalty}
                  </Button>
                )}
                <Button size="sm" disabled={paying || mixedCurrency} onClick={() => payIds([...selected])}
                  className="h-8 bg-orange-500 hover:bg-orange-600 text-white gap-2">
                  <CreditCard className="h-3.5 w-3.5" />
                  {paying ? t.openingStripe : t.payInvoices}
                </Button>
              </div>
            </div>
          )}

          {outstanding.length === 0 ? (
            <Card className="bg-zinc-900/60 border-zinc-800 rounded-2xl">
              <div className="p-10 text-center">
                <CheckCircle2 className="h-9 w-9 text-green-500 mx-auto mb-3" />
                <div className="text-base font-medium text-zinc-200">{t.allClear}</div>
                <div className="text-sm text-zinc-500 mt-1">{awaiting.length > 0 ? t.noInvoicesAwaiting : t.noInvoicesDue}</div>
              </div>
            </Card>
          ) : (
            <Card className="bg-zinc-900/60 border-zinc-800 rounded-2xl">
              <div className="p-2">
                {selfServeCard && (
                  <div className="flex items-center gap-3 px-3 pb-1.5 pt-1 border-b border-zinc-800 mb-1">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
                    <span className="text-[11px] text-zinc-500">{t.colInvoice}</span>
                  </div>
                )}
                {outstanding.map((row) => renderRow(row, "outstanding"))}
              </div>
            </Card>
          )}
          {!selfServeCard && <p className="text-xs text-zinc-500 px-1">{t.payByTransfer}</p>}
        </TabsContent>

        {awaiting.length > 0 && (
          <TabsContent value="awaiting" className="mt-3">
            <Card className="bg-zinc-900/60 border-zinc-800 rounded-2xl">
              <div className="p-2">{awaiting.map((row) => renderRow(row, "awaiting"))}</div>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="paid" className="mt-3">
          {paid.length === 0 ? (
            <Card className="bg-zinc-900/60 border-zinc-800 rounded-2xl">
              <div className="p-10 text-center text-sm text-zinc-500">{t.noPaymentsYet}</div>
            </Card>
          ) : (
            <Card className="bg-zinc-900/60 border-zinc-800 rounded-2xl">
              <div className="p-2">{paid.map((row) => renderRow(row, "paid"))}</div>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};


export default MyPaymentSchedulePage;
